import { useEffect } from 'react';
import type { Theme } from '@tg/shared';
import { useSession } from '@/stores/session';

export type ResolvedTheme = 'dark' | 'light';

export function systemPrefersLight(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: light)').matches;
  } catch {
    return false;
  }
}

export function resolveTheme(theme: Theme, prefersLight?: boolean): ResolvedTheme {
  // the OS query only for «system»: this runs on every render of every card / tile (useResolvedTheme)
  if (theme === 'system') return (prefersLight ?? systemPrefersLight()) ? 'light' : 'dark';
  return theme;
}

/** Sets [data-theme] on <html>; tokens.css switches every colour from it. */
export function applyTheme(theme: Theme, root: HTMLElement = document.documentElement): ResolvedTheme {
  const resolved = resolveTheme(theme);
  root.setAttribute('data-theme', resolved);
  return resolved;
}

/** Keeps <html data-theme> in sync with the session theme (and the OS when theme = system). */
export function useThemeSync(): void {
  const theme = useSession((s) => s.theme);
  useEffect(() => {
    applyTheme(theme);
    if (theme !== 'system') return;
    let mq: MediaQueryList;
    try {
      mq = window.matchMedia('(prefers-color-scheme: light)');
    } catch {
      return;
    }
    const on = () => applyTheme('system');
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, [theme]);
}

/** Current resolved theme for components that need palette data (canvas, SVG art). */
export function useResolvedTheme(): ResolvedTheme {
  const theme = useSession((s) => s.theme);
  return resolveTheme(theme);
}
